import { ChangeDetectionStrategy, Component, computed, DestroyRef, inject, signal } from '@angular/core';
import { HttpErrorResponse } from '@angular/common/http';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { ActivatedRoute } from '@angular/router';
import { HeaderService } from '@app/shared/services/header.service';
import { NavigationService } from '@app/shared/services/navigation/navigation.service';
import { MatCardModule } from '@angular/material/card';
import { MatExpansionModule } from '@angular/material/expansion';
import { MatTableDataSource } from '@angular/material/table';
import { MatTableModule } from '@angular/material/table';
import { PaginationComponent } from '@components/pagination/pagination';
import { NmsSuccessRateComponent } from './nms-success-rate';
import { intervalToDuration } from 'date-fns';
import { DatePipe, DecimalPipe } from '@angular/common';
import { MatButtonModule } from '@angular/material/button';
import { UserService } from '@services/user/user.service';
import { NmsService } from '@services/nms.service';
import { ConfirmationDialogService } from '@services/confirmation-dialog/confirmation-dialog.service';
import {
  BleKeyDeliveryNodeState,
  BLEKeyDeliveryProgressApiResponse,
  BLEKeyDeliveryStatus,
  BLEKeyDeliveryStepStatus,
  DeviceJoiningStatus,
  GatewayJoinStatusResponse,
  GatewayStatus,
  LatestOpenJoiningSessionStatus,
  NodeJoiningStatusRecord,
  OpenJoiningStatus
} from '@app/shared/models/nms.models';
import { catchError, EMPTY, exhaustMap, forkJoin, Subscription, takeWhile, timer } from 'rxjs';
import { MatIcon } from '@angular/material/icon';
import { MatTooltip } from '@angular/material/tooltip';
import { Clipboard } from '@angular/cdk/clipboard';
import { ToastService } from '@services/toast/toast.service';

type GatewayStatusRow = {
  gateway: string;
  gatewaySerialNumber: string | null;
  gatewayCode: string;
  status: GatewayStatus;
  deviceJoining: DeviceJoiningStatus;
  nodesJoined: number;
  lastJoinAt: Date | null;
  highlighted?: boolean;
};

type JoiningSessionAction = 'idle' | 'starting' | 'stopping';

@Component({
  selector: 'app-nms',
  standalone: true,
  changeDetection: ChangeDetectionStrategy.OnPush,
  imports: [
    MatCardModule,
    MatExpansionModule,
    PaginationComponent,
    NmsSuccessRateComponent,
    MatTableModule,
    DatePipe,
    DecimalPipe,
    MatButtonModule,
    MatIcon,
    MatTooltip
  ],
  templateUrl: './nms.component.html',
  styleUrls: ['./nms.component.scss']
})
export class NmsComponent {
  private readonly BLE_KEY_DELIVERY_POLLING_INTERVAL = 10000;
  private readonly route = inject(ActivatedRoute);
  private readonly nmsService = inject(NmsService);
  private readonly userService = inject(UserService);
  private readonly header = inject(HeaderService);
  private readonly navigationService = inject(NavigationService);
  private readonly confirmationDialogService = inject(ConfirmationDialogService);
  private readonly destroyRef = inject(DestroyRef);
  private readonly clipboard = inject(Clipboard);
  private readonly toast = inject(ToastService);
  private sessionPollingSubscription: Subscription | null = null;
  private bleKeyDeliveryPollingSubscription: Subscription | null = null;
  readonly contextError = signal('');
  readonly sessionActionError = signal('');

  readonly buildingId = signal(0);
  readonly loading = signal(true);
  readonly startJoining = signal(true);
  readonly openJoiningSessionId = signal<number | null>(null);
  readonly joiningSessionAction = signal<JoiningSessionAction>('idle');
  readonly joiningSessionDetailsVisible = signal(false);
  readonly isReadyForJoining = signal(false);
  readonly openJoiningStatus = signal<OpenJoiningStatus | null>(null);
  readonly latestSessionStatus = signal<LatestOpenJoiningSessionStatus | null>(null);
  readonly currentGatewayStatuses = signal<GatewayJoinStatusResponse[]>([]);
  readonly gatewayStatusRows = signal<GatewayStatusRow[]>([]);
  readonly gatewayStatusDataSource = new MatTableDataSource<GatewayStatusRow>([]);
  readonly gatewayColumns: string[] = ['gateway', 'status', 'deviceJoining', 'nodesJoined', 'lastJoin'];
  readonly nodeJoiningRows = signal<NodeJoiningStatusRecord[]>([]);
  readonly nodeJoiningDataSource = new MatTableDataSource<NodeJoiningStatusRecord>([]);
  readonly nodeJoiningColumns: string[] = ['nodeAddress', 'uid', 'status', 'gateway', 'firmware', 'lastActivity'];
  readonly nodeJoiningLoading = signal(false);
  readonly nodeJoiningError = signal('');
  readonly bleKeyDeliveryRetrying = signal(false);
  readonly bleKeyDeliveryError = signal('');
  readonly retries = signal(0);
  readonly allowRetry = signal(false);
  readonly bleKeyDeliveryStatus = BLEKeyDeliveryStatus;
  readonly BLEKeyDeliveryStepStatus = BLEKeyDeliveryStepStatus;
  readonly sessionHistoryRows = signal<LatestOpenJoiningSessionStatus[]>([]);
  readonly sessionHistoryDataSource = new MatTableDataSource<LatestOpenJoiningSessionStatus>([]);
  readonly sessionHistoryColumns: string[] = ['dateTime', 'duration', 'nodesJoined', 'successRate'];
  readonly bleKeyDeliveryResponse = signal<BLEKeyDeliveryProgressApiResponse>({
    steps: [
      {
        workflowStepId: 0,
        status: BLEKeyDeliveryStepStatus.Pending,
        message: ''
      }
    ],
    status: BLEKeyDeliveryStatus.NoOpenJoiningSession,
    openJoiningSessionId: 0,
    message: '',
    totalNodes: 0,
    workflowId: 0,
    completedNodes: 0,
    failedNodes: 0,
    updatedAt:''
  });
  readonly showBleKeyDeliveryPanel = computed(() => {
    const { status, totalNodes } = this.bleKeyDeliveryResponse();

    return (
      (status === BLEKeyDeliveryStatus.ActionRequired ||
        status === BLEKeyDeliveryStatus.InProgress ||
        status === BLEKeyDeliveryStatus.Completed) &&
      totalNodes > 0
    );
  });
  readonly bleKeyDeliveryBlockedMessage = computed<string | null>(() => {
    switch (this.bleKeyDeliveryResponse().status) {
      case BLEKeyDeliveryStatus.WaitingForOpenJoiningToFinish:
        return 'Please wait for current open joining session to finish...';
      case BLEKeyDeliveryStatus.Pending:
        return 'Collecting node information before commencing BLE transfer...';
      default:
        return null;
    }
  });

  constructor() {
    this.header.showSiteMenu();
    this.header.showBuildingsMenu();
    this.header.showUserMenu();
    this.header.showSessionMenu();
    this.navigationService.applyLocation(window.location.href);

    const buildingId = Number(this.route.snapshot.paramMap.get('buildingId') || 0);
    this.buildingId.set(buildingId);
    this.loadContext();
  }

  loadContext(): void {
    this.loading.set(true);
    this.contextError.set('');
    const buildingId = this.buildingId();

    forkJoin({
      building: this.userService.getBuilding(buildingId),
      openJoiningStatus: this.nmsService.isDeviceReadyForJoining(buildingId),
      currentGatewayStatuses: this.nmsService.getCurrentGatewayStatuses(buildingId),
      sessionHistoryRows: this.nmsService.getSessionHistory(buildingId),
      latestSessionStatus: this.nmsService.getLatestOpenJoiningSessionStatus(buildingId)
    }).subscribe({
      next: ({ building, openJoiningStatus, currentGatewayStatuses, sessionHistoryRows, latestSessionStatus }) => {
        this.navigationService.initNavigation(window.location.href, building);
        this.currentGatewayStatuses.set(currentGatewayStatuses);
        this.sessionHistoryRows.set(sessionHistoryRows);
        this.reconcileSessionState(openJoiningStatus, latestSessionStatus);
        this.loading.set(false);

        this.loadBLEKeyDeliveryDetails();
      },
      error: () => {
        this.contextError.set('Unable to load. Please try again later.');
        this.loading.set(false);
      }
    });
  }

  startDeviceJoining(): void {
    if (!this.isReadyForJoining() || this.joiningSessionAction() !== 'idle') {
      return;
    }

    this.joiningSessionAction.set('starting');
    this.sessionActionError.set('');

    forkJoin({
      openJoiningStatus: this.nmsService.isDeviceReadyForJoining(this.buildingId()),
      latestSessionStatus: this.nmsService.getLatestOpenJoiningSessionStatus(this.buildingId())
    })
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe({
        next: ({ openJoiningStatus, latestSessionStatus }) => {
          if (!this.reconcileSessionState(openJoiningStatus, latestSessionStatus)) {
            if (this.joiningSessionAction() === 'starting') {
              this.joiningSessionAction.set('idle');
            }
            return;
          }

          this.nmsService
            .startDeviceJoining(this.buildingId())
            .pipe(takeUntilDestroyed(this.destroyRef))
            .subscribe({
              next: () => {
                this.startJoining.set(false);
                this.isReadyForJoining.set(false);
                this.loadBLEKeyDeliveryDetails();
                this.pollSessionState();
              },
              error: (err: HttpErrorResponse) => {
                this.showJoiningError(err, 'Failed to start device joining.');
                this.joiningSessionAction.set('idle');
              },
              complete: () => {
                if (this.joiningSessionAction() === 'starting') {
                  this.joiningSessionAction.set('idle');
                }
              }
            });
        },
        error: (err: HttpErrorResponse) => {
          this.showJoiningError(err, 'Failed to start device joining.');
          this.joiningSessionAction.set('idle');
        }
      });
  }

  stopDeviceJoining(): void {
    if (this.joiningSessionAction() !== 'idle') {
      return;
    }

    this.joiningSessionAction.set('stopping');
    this.sessionActionError.set('');

    this.nmsService
      .stopDeviceJoining(this.buildingId())
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe({
        next: () => {
          this.pollSessionState();
        },
        error: (err: HttpErrorResponse) => {
          this.showJoiningError(err, 'Failed to stop device joining.');
          this.pollSessionState();
        }
      });
  }

  private showJoiningError(err: HttpErrorResponse, fallback: string): void {
    const message = this.readJoiningErrorMessage(err) ?? fallback;
    this.confirmationDialogService.error({ message, showConfirm: false });
  }

  private readJoiningErrorMessage(err: HttpErrorResponse): string | null {
    if (typeof err.error === 'string') {
      return err.error;
    }

    if (err.error && typeof err.error === 'object' && 'message' in err.error && typeof err.error.message === 'string') {
      return err.error.message;
    }

    return null;
  }

  toggleJoiningSessionDetails(): void {
    this.joiningSessionDetailsVisible.update((visible) => !visible);
  }

  private pollSessionState(): void {
    this.sessionPollingSubscription?.unsubscribe();
    this.sessionPollingSubscription = timer(0, 5_000)
      .pipe(
        exhaustMap(() =>
          forkJoin({
            openJoiningStatus: this.nmsService.isDeviceReadyForJoining(this.buildingId()),
            latestSessionStatus: this.nmsService.getLatestOpenJoiningSessionStatus(this.buildingId())
          }).pipe(
            catchError(() => {
              this.sessionActionError.set('Unable to update the device joining session.');
              return EMPTY;
            })
          )
        ),
        takeWhile(({ openJoiningStatus, latestSessionStatus }) => {
          this.applySessionState(openJoiningStatus, latestSessionStatus);

          if (this.sessionStateNeedsPolling(openJoiningStatus, latestSessionStatus)) {
            this.joiningSessionAction.set('stopping');
            this.sessionActionError.set('');
            return true;
          }

          if (latestSessionStatus.status === 'ACTIVE') {
            this.joiningSessionAction.set('idle');
            this.sessionActionError.set('');
            return true;
          }

          this.joiningSessionAction.set('idle');
          this.sessionActionError.set('');

          const sessionEnded =
            openJoiningStatus.noActiveSession &&
            latestSessionStatus.status !== 'ACTIVE' &&
            latestSessionStatus.status !== 'ENDING';

          if (sessionEnded) {
            this.startBLEKeyDeliveryPolling();
          }

          return false;
        }),
        takeUntilDestroyed(this.destroyRef)
      )
      .subscribe({
        complete: () => {
          this.sessionPollingSubscription = null;
        }
      });
  }

  private reconcileSessionState(
    openJoiningStatus: OpenJoiningStatus,
    latestSessionStatus: LatestOpenJoiningSessionStatus
  ): boolean {
    this.applySessionState(openJoiningStatus, latestSessionStatus);

    if (this.sessionStateNeedsPolling(openJoiningStatus, latestSessionStatus)) {
      this.joiningSessionAction.set('stopping');
      this.pollSessionState();
      return false;
    }

    if (latestSessionStatus.status === 'ACTIVE') {
      this.joiningSessionAction.set('idle');
      this.pollSessionState();
      return false;
    }

    return openJoiningStatus.ready && openJoiningStatus.noActiveSession && latestSessionStatus.status !== 'ACTIVE';
  }

  private sessionStateNeedsPolling(
    openJoiningStatus: OpenJoiningStatus,
    latestSessionStatus: LatestOpenJoiningSessionStatus
  ): boolean {
    const latestSessionIsActive = latestSessionStatus.status === 'ACTIVE';
    return latestSessionStatus.status === 'ENDING' || latestSessionIsActive === openJoiningStatus.noActiveSession;
  }

  private applySessionState(
    openJoiningStatus: OpenJoiningStatus,
    latestSessionStatus: LatestOpenJoiningSessionStatus
  ): void {
    this.applyOpenJoiningStatus(openJoiningStatus);
    this.latestSessionStatus.set(latestSessionStatus);
    this.nodeJoiningRows.set(this.nmsService.mapNodeJoiningStatusRows(latestSessionStatus));
    this.gatewayStatusRows.set(this.mapGatewayStatusRows(latestSessionStatus, this.currentGatewayStatuses()));

    if (latestSessionStatus.status === 'ACTIVE' || latestSessionStatus.status === 'ENDING') {
      this.startJoining.set(false);
      this.isReadyForJoining.set(false);
    }
  }

  private applyOpenJoiningStatus(openJoiningStatus: OpenJoiningStatus): void {
    this.openJoiningStatus.set(openJoiningStatus);
    this.startJoining.set(openJoiningStatus.noActiveSession);
    this.isReadyForJoining.set(openJoiningStatus.ready);
  }

  loadBLEKeyDeliveryDetails(): void {
    this.nmsService.loadBLEKeyDelivery(this.buildingId())
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe({
        next: (bleKeyDeliveryResponse) => {
          this.openJoiningSessionId.set(bleKeyDeliveryResponse.openJoiningSessionId);
          this.bleKeyDeliveryResponse.set(bleKeyDeliveryResponse);
          this.updateBleKeyDeliveryRetryState();
          this.bleKeyDeliveryError.set('');
        },
        error: () => {
          this.bleKeyDeliveryError.set('Unable to load BLE key delivery status. Please try again later.');
        }
      });
  }

  retryBleKeyDelivery(): void {
    const workflowId = this.bleKeyDeliveryResponse().workflowId;
    if (!this.allowRetry() || !workflowId || this.bleKeyDeliveryRetrying()) {
      return;
    }

    this.bleKeyDeliveryRetrying.set(true);
    this.bleKeyDeliveryError.set('');
    this.nmsService
      .retryBLEKeyDelivery(this.buildingId(), workflowId)
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe({
        next: () => {
          this.bleKeyDeliveryRetrying.set(false);
          this.startBLEKeyDeliveryPolling();
        },
        error: () => {
          this.bleKeyDeliveryError.set('Unable to retry BLE key delivery. Please try again later.');
          this.bleKeyDeliveryRetrying.set(false);
        }
      });
  }

  private startBLEKeyDeliveryPolling(): void {
    this.stopBLEKeyDeliveryPolling();
    this.bleKeyDeliveryError.set('');
    this.bleKeyDeliveryPollingSubscription = timer(0, this.BLE_KEY_DELIVERY_POLLING_INTERVAL)
      .pipe(
        exhaustMap(() =>
          this.nmsService.loadBLEKeyDelivery(this.buildingId()).pipe(
            catchError(() => {
              this.bleKeyDeliveryError.set('Unable to load BLE key delivery status. Please try again later.');

              return EMPTY;
            })
          )
        ),
        takeWhile(
          (response) =>
            response.status !== BLEKeyDeliveryStatus.Completed &&
            response.status !== BLEKeyDeliveryStatus.ActionRequired &&
            response.status !== BLEKeyDeliveryStatus.Failed &&
            response.status !== BLEKeyDeliveryStatus.Cancelled,
          true
        ),
        takeUntilDestroyed(this.destroyRef)
      )
      .subscribe({
        next: (bleKeyDeliveryResponse) => {
          this.openJoiningSessionId.set(bleKeyDeliveryResponse.openJoiningSessionId);
          this.bleKeyDeliveryResponse.set(bleKeyDeliveryResponse);
          this.updateBleKeyDeliveryRetryState();
          this.bleKeyDeliveryError.set('');
        },
        complete: () => {
          this.bleKeyDeliveryPollingSubscription = null;
        }
      });
  }

  private stopBLEKeyDeliveryPolling(): void {
    this.bleKeyDeliveryPollingSubscription?.unsubscribe();
    this.bleKeyDeliveryPollingSubscription = null;
  }

  private updateBleKeyDeliveryRetryState(): void {
    this.retries.set(this.bleKeyDeliveryResponse().steps.length - 1);
    this.allowRetry.set(this.bleKeyDeliveryResponse().status === BLEKeyDeliveryStatus.ActionRequired);
  }

  formatDuration(seconds: number): string {
    if (!seconds || seconds === 0) {
      return '0s'; // for failed OJ sessions
    }
    const duration = intervalToDuration({ start: 0, end: seconds * 1000 });
    const formatted = [
      duration.days && `${duration.days}d`,
      duration.hours && `${duration.hours}h`,
      duration.minutes && `${duration.minutes}m`,
      duration.seconds && `${duration.seconds}s`
    ]
      .filter(Boolean)
      .join(' ');
    return formatted;
  }

  copyNodesToClipboard(): void {
    this.nmsService
      .getBuildingNodesState(this.buildingId(), this.openJoiningSessionId())
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe({
        next: (nodes: BleKeyDeliveryNodeState[]) => {
          const csvFormat = 'Node Address(Decimal), Node Address(Hex), Status \n';
          const copiedNodes = nodes
            .map((node: BleKeyDeliveryNodeState) => {
              return `${node.nodeAddress} , ${node.nodeAddress.toString(16).toLocaleUpperCase()} , ${node.status}`;
            })
            .join('\n');
          this.clipboard.copy(`${csvFormat} ${copiedNodes}`);
          this.toast.success({ message: 'Nodes copied to clipboard.', dataCy: 'send-success-toast' });
        },
        error: () => {
          this.toast.error({ message: 'Unable to copy nodes to clipboard.', dataCy: 'send-error-toast' });
        }
      });
  }

  private mapGatewayStatusRows(
    sessionStatus: LatestOpenJoiningSessionStatus,
    currentGatewayStatuses: GatewayJoinStatusResponse[]
  ): GatewayStatusRow[] {
    const gatewayActivityByUuid = new Map<string, { nodesJoined: number; lastJoinAt: Date | null }>();
    const currentGatewayStatusByUuid = new Map(
      currentGatewayStatuses.map((gatewayStatus) => [gatewayStatus.uuid, gatewayStatus])
    );

    sessionStatus.nodeStatuses.forEach((nodeStatus) => {
      const gatewayUuid = nodeStatus.nodeStatusResponse?.nodeStatistics?.lastMessageGatewayUuid;
      if (!gatewayUuid || !nodeStatus.provisioningStatus) {
        return;
      }

      const lastMessageRxTime = nodeStatus.nodeStatusResponse?.nodeStatistics?.lastMessageRxTime ?? 0;
      const lastJoinAt = lastMessageRxTime ? new Date(lastMessageRxTime * 1000) : null;
      const previous = gatewayActivityByUuid.get(gatewayUuid);

      gatewayActivityByUuid.set(gatewayUuid, {
        nodesJoined: nodeStatus.provisioningStatus === 'PROVISIONED' ? (previous?.nodesJoined ?? 0) + 1 : (previous?.nodesJoined ?? 0),
        lastJoinAt: this.maxDate(previous?.lastJoinAt ?? null, lastJoinAt)
      });
    });

    const sessionGatewayRows = sessionStatus.gatewayStatuses.map((sessionGatewayStatus) => {
      const currentGatewayStatus = currentGatewayStatusByUuid.get(sessionGatewayStatus.gatewayUuid);
      const gatewayActivity = gatewayActivityByUuid.get(sessionGatewayStatus.gatewayUuid);
      const gatewaySerialNumber = sessionGatewayStatus.gatewaySerialNumber?.trim() || null;
      const gatewayName =
        sessionGatewayStatus.gatewayName?.trim() ||
        gatewaySerialNumber ||
        sessionGatewayStatus.gatewayJoinStatusResponse.gatewayId;

      return {
        gateway: gatewayName,
        gatewaySerialNumber,
        gatewayCode: sessionGatewayStatus.gatewayUuid,
        status: this.mapGatewayHealthStatus(currentGatewayStatus?.gatewayStatus ?? sessionGatewayStatus.status),
        deviceJoining: this.mapDeviceJoiningStatus(
          currentGatewayStatus?.joiningState ?? sessionGatewayStatus.joiningState
        ),
        nodesJoined: gatewayActivity?.nodesJoined ?? 0,
        lastJoinAt: gatewayActivity?.lastJoinAt ?? null
      };
    });

    const sessionGatewayUuids = new Set(
      sessionStatus.gatewayStatuses.map((gatewayStatus) => gatewayStatus.gatewayUuid)
    );
    const liveOnlyGatewayRows = currentGatewayStatuses
      .filter((gatewayStatus) => !sessionGatewayUuids.has(gatewayStatus.uuid))
      .map((gatewayStatus) => {
        const gatewaySerialNumber = gatewayStatus.gatewayId?.trim() || null;

        return {
          gateway: gatewayStatus.name?.trim() || gatewaySerialNumber || gatewayStatus.uuid || 'Unknown gateway',
          gatewaySerialNumber,
          gatewayCode: gatewayStatus.uuid,
          status: this.mapGatewayHealthStatus(gatewayStatus.gatewayStatus),
          deviceJoining: this.mapDeviceJoiningStatus(gatewayStatus.joiningState),
          nodesJoined: 0,
          lastJoinAt: null
        };
      });

    return [...sessionGatewayRows, ...liveOnlyGatewayRows];
  }

  private mapGatewayHealthStatus(status: GatewayJoinStatusResponse['gatewayStatus']): GatewayStatus {
    switch ((status ?? '').toUpperCase()) {
      case 'ACTIVE':
        return 'HEALTHY';
      case 'OFFLINE':
        return 'OFFLINE';
      default:
        return 'DEGRADED';
    }
  }

  private mapDeviceJoiningStatus(joiningState: GatewayJoinStatusResponse['joiningState']): DeviceJoiningStatus {
    return (joiningState ?? '').toUpperCase() === 'ENABLED' ? 'ACTIVE' : 'INACTIVE';
  }

  private maxDate(first: Date | null, second: Date | null): Date | null {
    if (!first) {
      return second;
    }

    if (!second) {
      return first;
    }

    return first.getTime() >= second.getTime() ? first : second;
  }
}
